// frontend/src/components/UploadPage.js
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faSpinner, faUpload, faCheck, faTimes, faTrash, faSync, faExclamationTriangle, faSearch } from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://uio-poreznik-production.up.railway.app/api';

const UploadPage = () => {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [deleteStatus, setDeleteStatus] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Debug search term changes
  useEffect(() => {
    console.log('Search term changed to:', searchTerm);
  }, [searchTerm]);

  const onDrop = useCallback((acceptedFiles) => {
    // Filter for PDF files
    const pdfFiles = acceptedFiles.filter(file => file.type === 'application/pdf');
    setFiles(prevFiles => [...prevFiles, ...pdfFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1536 * 1536 // 10MB
  });

  // Fetch documents when component mounts
  useEffect(() => {
    fetchDocuments();
  }, []);

  // useEffect(() => {
  //   // Clean up all polling intervals when component unmounts
  //   return () => {
  //     Object.values(pollingIntervals).forEach(intervalId => {
  //       clearInterval(intervalId);
  //     });
  //   };
  // }, [pollingIntervals]);

  const testBackendConnection = async () => {
    try {
      // Test backend connection
      const testUrl = `${API_URL.split('/api')[0]}/api`;
      console.log('Testing backend connection to:', testUrl);
      
      const response = await axios.get(testUrl);
      console.log('Backend connection test:', response);
      
      // Try to get Pinecone status as well
      try {
        const pineconeResponse = await axios.get(`${API_URL.split('/api')[0]}/api/documents/pinecone-status`);
        console.log('Pinecone status:', pineconeResponse.data);
        
        if (pineconeResponse.data.success) {
          alert(`Successfully connected to backend and Pinecone!\nVectors in database: ${pineconeResponse.data.stats.totalVectors || 0}`);
        } else {
          alert(`Successfully connected to backend but Pinecone connection failed: ${pineconeResponse.data.error}`);
        }
      } catch (pineconeError) {
        console.error('Pinecone status check failed:', pineconeError);
        alert('Successfully connected to backend! But Pinecone status check failed.');
      }
    } catch (error) {
      console.error('Backend connection test failed:', error);
      
      let errorMessage = 'Failed to connect to backend';
      if (error.response) {
        errorMessage = `Server responded with error: ${error.response.status} - ${error.response.data.message || error.message}`;
      } else if (error.request) {
        errorMessage = 'No response received from server. Is the backend running?';
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      alert(`Backend connection test failed: ${errorMessage}`);
    } 
  }

  const fetchDocuments = async () => {
    try {
      setDocumentsLoading(true);
      setDocumentsError(null);
      
      // Fix the API URL to ensure we're calling the correct endpoint
      const apiUrl = `${API_URL.split('/api')[0]}/api/documents/list`;
      console.log("Fetching documents from:", apiUrl);
      
      const response = await axios.get(apiUrl);
      console.log("Document list response:", response.data);
      
      if (response.data.success) {
        setDocuments(response.data.documents || []);
      } else {
        console.error("API returned success:false", response.data);
        setDocumentsError(response.data.error || 'Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      
      let errorMessage = 'Failed to load documents';
      
      if (error.response) {
        // Server responded with an error status
        console.error("Server error response:", error.response.data);
        errorMessage = error.response.data.message || error.response.data.error || `Error ${error.response.status}: ${error.message}`;
      } else if (error.request) {
        // Request was made but no response received
        console.error("No response received:", error.request);
        errorMessage = 'No response from server - check API connection';
      } else {
        // Error in setting up the request
        console.error("Request error:", error.message);
        errorMessage = error.message;
      }
      
      setDocumentsError(errorMessage);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const deleteDocument = async (documentName) => {
    try {
      // Ask for confirmation before deleting
      const confirmed = window.confirm(`Are you sure you want to delete the document "${documentName}"? This action cannot be undone.`);
      
      if (!confirmed) {
        console.log(`Document deletion cancelled by user: ${documentName}`);
        return;
      }
      
      // Update status to show deletion in progress
      setDeleteStatus(prev => ({
        ...prev,
        [documentName]: 'deleting'
      }));
      
      // Construct the correct URL with the base API URL
      const deleteUrl = `${API_URL.split('/api')[0]}/api/documents/delete/${encodeURIComponent(documentName)}`;
      console.log("Deleting document using URL:", deleteUrl);
      
      const response = await axios.delete(deleteUrl);
      console.log("Delete response:", response.data);
      
      if (response.data.success) {
        // Update status to show successful deletion
        setDeleteStatus(prev => ({
          ...prev,
          [documentName]: 'success'
        }));
        
        // Show result message
        if (response.data.deletedCount) {
          alert(`Successfully deleted document "${documentName}" (${response.data.deletedCount} chunks)`);
        }
        
        // Remove document from list after a delay to show success status
        setTimeout(() => {
          setDocuments(prev => prev.filter(doc => doc.name !== documentName));
          setDeleteStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[documentName];
            return newStatus;
          });
        }, 1500);
      } else {
        // Update status to show failed deletion
        setDeleteStatus(prev => ({
          ...prev,
          [documentName]: 'error'
        }));
        console.error("Delete failed with response:", response.data);
        alert(`Failed to delete document: ${response.data.message || response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error deleting document ${documentName}:`, error);
      // Log detailed error information
      if (error.response) {
        console.error("Server error response:", error.response.data);
      }
      
      // Update status to show failed deletion
      setDeleteStatus(prev => ({
        ...prev,
        [documentName]: 'error'
      }));
      
      alert(`Error deleting document: ${error.message}`);
    }
  };

  // Check if a document with the same name already exists
  const checkDocumentExists = (fileName) => {
    return documents.some(doc => doc.name === fileName);
  };

  // Confirm document replacement with the user
  const confirmDocumentReplacement = (fileName) => {
    return window.confirm(`Dokument "${fileName}" već postoji. Da li želite da ga zamijenite?`);
  };
  
  const uploadFile = async (file) => {
    // Check if document with same name already exists
    if (checkDocumentExists(file.name)) {
      // Document exists, ask for confirmation
      const confirmed = confirmDocumentReplacement(file.name);
      
      if (!confirmed) {
        console.log(`Upload of ${file.name} cancelled by user to avoid overwriting existing document`);
        
        setUploadStatus(prev => ({
          ...prev,
          [file.name]: 'cancelled'
        }));
        
        return {
          success: false,
          cancelled: true,
          message: 'Upload cancelled by user'
        };
      }
      
      console.log(`User confirmed replacing document ${file.name}`);
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploadStatus(prev => ({
        ...prev,
        [file.name]: 'uploading'
      }));
      
      // Start 20-minute progress bar
      let progress = 0;
      const totalTime = 20 * 60 * 1000; // 20 minutes in milliseconds
      const updateInterval = 1000; // Update every second
      const progressIncrement = 100 / (totalTime / updateInterval);
      
      const progressInterval = setInterval(() => {
        progress += progressIncrement;
        
        if (progress >= 100) {
          progress = 100;
          clearInterval(progressInterval);
          
          // Show success message after 20 minutes
          setUploadStatus(prev => ({
            ...prev,
            [file.name]: 'success'
          }));
          
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: 100
          }));
          
          // Refresh documents after completion
          setTimeout(() => fetchDocuments(), 1000);
        } else {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: Math.round(progress)
          }));
        }
      }, updateInterval);
      
      // Use existing upload endpoint
      const uploadUrl = `${API_URL.split('/api')[0]}/api/documents/upload-sync`;
      console.log("Sending request to: ", uploadUrl);
      
      // Start the upload (but don't wait for it to complete)
      axios.post(uploadUrl, formData, {
        timeout: 25000, // 25 second timeout
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(() => {
        console.log(`Upload of ${file.name} completed successfully`);
        // Don't change status here - let the 20-minute timer handle it
      })
      .catch(error => {
        console.error(`Upload error or timeout: ${error.message}`);
        // Don't change status here - let the 20-minute timer handle it
      });
      
      // Return immediately
      return { success: true, message: 'Upload started' };
    } catch (error) {
      console.error('Error starting upload:', error);
      setUploadStatus(prev => ({
        ...prev,
        [file.name]: 'error'
      }));
      return { success: false, error: error.message };
    }
  };

  const uploadAll = async () => {
    // Check for duplicates first and show alert if needed
    const existingFiles = files.filter(file => checkDocumentExists(file.name));
    
    if (existingFiles.length > 0) {
      const fileNames = existingFiles.map(file => file.name).join(', ');
      const confirmMsg = existingFiles.length === 1
        ? `Dokument "${fileNames}" već postoji u bazi. Da li želite da ga zamijenite?`
        : `Sljedeći dokumenti već postoje u bazi: ${fileNames}. Da li želite da ih zamijenite?`;
      
      if (!window.confirm(confirmMsg)) {
        console.log('Batch upload cancelled due to existing documents');
        
        // Mark cancelled files
        existingFiles.forEach(file => {
          setUploadStatus(prev => ({
            ...prev,
            [file.name]: 'cancelled'
          }));
        });
        
        // Upload only non-existing files
        for (const file of files) {
          if (!checkDocumentExists(file.name) && !uploadStatus[file.name]) {
            await uploadFile(file);
          }
        }
        
        return;
      }
    }
    
    // Upload files one by one to not overload the server
    for (const file of files) {
      if (!uploadStatus[file.name]) {
        await uploadFile(file);
      }
    }
  };

  const removeFile = (fileName) => {
    setFiles(prevFiles => prevFiles.filter(file => file.name !== fileName));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[fileName];
      return newStatus;
    });
  };

  const getStatusIcon = (fileName) => {
    const status = uploadStatus[fileName];
    
    if (status === 'uploading') {
      return <FontAwesomeIcon icon={faSpinner} spin />;
    } else if (status === 'success') {
      return <FontAwesomeIcon icon={faCheck} style={{ color: '#4ade80' }} />;
    } else if (status === 'error') {
      return <FontAwesomeIcon icon={faTimes} style={{ color: '#ef4444' }} />;
    } else if (status === 'cancelled') {
      return <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b' }} />;
    }
    
    // Check for potential conflicts with existing documents
    const exists = checkDocumentExists(fileName);
    
    return exists ? 
      <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b' }} title="Dokument već postoji" /> : 
      <FontAwesomeIcon icon={faUpload} />;
  };

  const getDeleteStatusIcon = (documentName) => {
    const status = deleteStatus[documentName];
    
    if (status === 'deleting') {
      return <FontAwesomeIcon icon={faSpinner} spin />;
    } else if (status === 'success') {
      return <FontAwesomeIcon icon={faCheck} style={{ color: '#4ade80' }} />;
    } else if (status === 'error') {
      return <FontAwesomeIcon icon={faTimes} style={{ color: '#ef4444' }} />;
    }
    
    return <FontAwesomeIcon icon={faTrash} />;
  };

  // Format date to be more readable
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown date';
      
      // Parse the date - handle PostgreSQL timestamp format
      const date = new Date(dateString);
      
      // Check for invalid date
      if (isNaN(date.getTime())) {
        console.log(`Invalid date format: ${dateString}`);
        return 'Unknown date';
      }
      
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (e) {
      console.error(`Error formatting date: ${dateString}`, e);
      return 'Unknown date';
    }
  };

  // Search documents by NAME in the table list
  const filteredDocuments = useMemo(() => {
    console.log('=== FILTERING DOCUMENTS ===');
    console.log('Current searchTerm:', searchTerm);
    console.log('Total documents:', documents.length);
    
    // If no search term, show all documents (including duplicates)
    if (!searchTerm || searchTerm.trim() === '') {
      console.log('No search term, returning all documents');
      return documents;
    }
    
    // Filter documents by document NAME only
    const searchTermLower = searchTerm.toLowerCase().trim();
    console.log('Searching for (lowercase):', searchTermLower);
    
    const filtered = documents.filter(doc => {
      const documentName = (doc.name || '').toLowerCase();
      const nameMatches = documentName.includes(searchTermLower);
      
      console.log(`Document: "${doc.name}" -> lowercase: "${documentName}" -> matches "${searchTermLower}": ${nameMatches}`);
      return nameMatches;
    });
    
    console.log(`=== FILTER RESULT: ${filtered.length} of ${documents.length} documents ===`);
    return filtered;
  }, [documents, searchTerm]);

  // Force component update when searchTerm changes
  const handleSearchChange = useCallback((newValue) => {
    console.log('handleSearchChange called with:', newValue);
    setSearchTerm(newValue);
  }, []);

  return (
    <div className="chat-container">
      <div className="message-container">
        <div className="messages" style={{ justifyContent: 'flex-start' }}>
          <div className="welcome-message">
            <h3>Učitaj dokumente</h3>
            <p>Učitajte PDF dokumente za analizu i pretragu</p>
          </div>
          
          <div className="upload-container">
            <div className="upload-section" style={{ marginBottom: '2rem' }}>
              <button 
                className="button" 
                onClick={testBackendConnection}
                style={{ marginBottom: '20px', marginLeft: 'auto', display: 'block' }}
              >
                Testiraj konekciju s backendom
              </button>
              
              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'active' : ''}`}
              >
                <input {...getInputProps()} />
                <p>Prevucite i spustite PDF fajlove ovdje, ili kliknite da ih odaberete</p>
                <p className="dropzone-info" style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  Maksimalna veličina fajla: 10MB
                </p>
              </div>
              
              {files.length > 0 && (
                <div className="file-list">
                  <h3 style={{ color: '#fff', fontWeight: '500', marginBottom: '1rem', fontSize: '1rem' }}>
                    Odabrani fajlovi
                  </h3>
                  {files.map((file) => {
                    const fileExists = checkDocumentExists(file.name);
                    return (
                      <div key={file.name} className="file-item">
                        <div className="file-icon">
                          <FontAwesomeIcon icon={faFilePdf} />
                        </div>
                        <div className="file-name">
                          {file.name}
                          {fileExists && uploadStatus[file.name] !== 'uploading' && uploadStatus[file.name] !== 'success' && (
                            <div style={{ fontSize: '0.8rem', color: '#f59e0b', marginTop: '0.25rem' }}>
                              Ovaj dokument već postoji u bazi
                            </div>
                          )}
                          {uploadStatus[file.name] === 'cancelled' && (
                            <div style={{ fontSize: '0.8rem', color: '#f59e0b', marginTop: '0.25rem' }}>
                              Učitavanje otkazano
                            </div>
                          )}
                          {uploadStatus[file.name] === 'success' && (
                            <div style={{ fontSize: '0.8rem', color: '#4ade80', marginTop: '0.25rem' }}>
                              Uspjesno uploadovano
                            </div>
                          )}
                        </div>
                        <div className="file-status">
                          {getStatusIcon(file.name)}
                        </div>
                        <button 
                          className="button"
                          onClick={() => removeFile(file.name)}
                          disabled={uploadStatus[file.name] === 'uploading'}
                          style={{ padding: '0.4rem 0.75rem', marginLeft: '0.5rem' }}
                        >
                          Ukloni
                        </button>
                        {uploadProgress[file.name] !== undefined && uploadProgress[file.name] < 100 && (
                          <div className="upload-progress" style={{ marginTop: '0.5rem', height: '3px', width: '100%', backgroundColor: '#333', borderRadius: '3px', overflow: 'hidden' }}>
                            <div 
                              className="progress-bar"
                              style={{ width: `${uploadProgress[file.name]}%`, height: '100%', backgroundColor: '#0070f3' }}
                            ></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  <button 
                    className="button primary"
                    onClick={uploadAll}
                    disabled={files.length === 0 || Object.values(uploadStatus).includes('uploading')}
                    style={{ marginTop: '1rem' }}
                  >
                    Učitaj sve
                  </button>
                </div>
              )}
              
              {Object.entries(uploadStatus).some(([_, status]) => status === 'success') && (
                <div className="status-message success" style={{ marginTop: '1rem', padding: '1rem', borderRadius: '4px', backgroundColor: 'rgba(74, 222, 128, 0.1)', color: '#4ade80' }}>
                  <p>Uspješno učitani dokumenti! Sada možete razgovarati s njima.</p>
                </div>
              )}
              
              {Object.entries(uploadStatus).some(([_, status]) => status === 'error') && (
                <div className="status-message error" style={{ marginTop: '1rem', padding: '1rem', borderRadius: '4px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                  <p>Neki fajlovi nisu uspješno učitani. Molimo pokušajte ponovo.</p>
                </div>
              )}
            </div>
            
            {/* Document Management Section */}
            <div className="document-management" style={{ marginTop: '2rem', borderTop: '1px solid #333', paddingTop: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontWeight: '500', fontSize: '1.2rem' }} className="input-color">
                  Upravljanje dokumentima
                </h3>
                <button 
                  className="button"
                  onClick={fetchDocuments}
                  disabled={documentsLoading}
                  style={{ padding: '0.4rem 0.75rem' }}
                >
                  {documentsLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <><FontAwesomeIcon icon={faSync} /> Osvježi</>
                  )}
                </button>
              </div>

              {/* Search Input */}
              {documents.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ position: 'relative' }}>
                    <FontAwesomeIcon 
                      icon={faSearch} 
                      style={{ 
                        position: 'absolute', 
                        left: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        color: '#888',
                        zIndex: 1
                      }} 
                    />
                    <input
                      key="document-search-input"
                      type="text"
                      placeholder="Pretražite dokumente..."
                      value={searchTerm}
                      className="input-color"
                      onChange={(e) => {
                        const newValue = e.target.value;
                        console.log('Search input changed to:', newValue);
                        handleSearchChange(newValue);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid #555',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  {searchTerm && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#888' }}>
                      Searching for: "{searchTerm}" (Length: {searchTerm.length})
                    </div>
                  )}
                </div>
              )}
              
              {documentsError && (
                <div className="status-message error" style={{ padding: '1rem', borderRadius: '4px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', marginBottom: '1rem' }}>
                  <p><FontAwesomeIcon icon={faExclamationTriangle} /> {documentsError}</p>
                </div>
              )}
              
              {documentsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  <p style={{ marginTop: '1rem', color: '#888' }}>Učitavanje dokumenata...</p>
                </div>
              ) : documents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
                  <p style={{ color: '#888' }}>Nema učitanih dokumenata</p>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                    Učitajte PDF dokumente koristeći obrazac iznad
                  </p>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}>
                  <p style={{ color: '#888' }}>Nema dokumenata koji odgovaraju pretrazi</p>
                  <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                    Pokušajte s drugim ključnim riječima
                  </p>
                </div>
              ) : (
                <div className="document-list">
                  <div className="document-header" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 120px 100px', 
                    padding: '0.75rem', 
                    borderBottom: '1px solid #333',
                    fontWeight: 'bold',
                    color: '#aaa'
                  }}>
                    <div>Dokument</div>
                    <div>Datum učitavanja</div>
                    <div>Akcije</div>
                  </div>
                  
                  {filteredDocuments.map((doc, index) => (
                    <div key={`doc-${index}-${doc.name}`} className="document-item" style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 120px 100px', 
                      padding: '0.75rem',
                      borderBottom: '1px solid #222',
                      alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: '0.75rem', color: '#ff6b6b' }} />
                        <div>
                          <div style={{ fontWeight: '500' }}>{doc.name}</div>
                          <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>
                            {doc.chunks} {doc.chunks === 1 ? 'chunk' : 'chunks'}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ fontSize: '0.85rem', color: '#aaa' }}>
                        {formatDate(doc.upload_date)}
                      </div>
                      
                      <div>
                        <button 
                          className="button"
                          onClick={() => deleteDocument(doc.name)}
                          disabled={deleteStatus[doc.name] === 'deleting'}
                          style={{ 
                            padding: '0.4rem 0.75rem',
                            backgroundColor: deleteStatus[doc.name] === 'error' ? '#8B0000' : 
                                            deleteStatus[doc.name] === 'success' ? '#2E8B57' : 
                                            'transparent'
                          }}
                          title="Izbriši dokument"
                        >
                          {getDeleteStatusIcon(doc.name)}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Show search results count */}
              {searchTerm && documents.length > 0 && (
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#888', textAlign: 'center' }}>
                  Prikazano {filteredDocuments.length} od {documents.length} dokumenata
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
